import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Row } from 'react-flexbox-grid'
import set from 'lodash/set'
import unset from 'lodash/unset'
import cloneDeep from 'lodash/cloneDeep'

import { loadAppConstants } from 'gql/query'
import globalStyles from 'style/app.module.scss'
import { Fonts } from 'sections/product/pages/Presentation/fieldsOptions'
import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import { Checkbox, Form, Number as NumberInput, Radio, Select, Text } from 'components/forms'
import Divider from 'components/Divider'
import Button from 'components/Button'
import { ReactComponent as LoadingIcon } from 'components/Loading/loading.svg'
import { useMutation } from 'components/graphql'
import SmartRadioGroup from 'components/forms/Radio/SmartRadioGroup'
import { Modal } from 'components/Modal'

import retryCopyCashRegisterSettings from '../../gql/copyCashRegisterSettings'
import windowsLookPagingImage from './img/windows-option-paging.png'
import windowsLookRegularImage from './img/windows-option-regular.png'
import { DisplayArticlesValues } from './utils/utils'
import { StyledColAlignCenter, StyledLabesStatus } from './styled'
import PaymentTerminals from './PaymentTerminals'
import Android from './Android'
import SelfCheckout from './SelfCheckout'
import Tse from './Tse'
import Printers from './Printers'

const Hardware = ({ data, formData, mutations: { updateItem }, handleMutation, appData, queryResult }) => {
	const { id, deviceType, parameters = {}, deviceIdentity, shopId, status } = data
	const [showFullPicture, setShowFullPicture] = useState(
		{
			image: windowsLookRegularImage,
			isEnabled: false
		}
	)

	useEffect(() => {
		if(status === 'UPDATING_DATA') {
			queryResult.startPolling(5000)
		} else {
			queryResult.stopPolling()
		}
	}, [status])

	const [copyCashRegisterSettings] = useMutation(retryCopyCashRegisterSettings, {
		variables: { id }
	})


	const [t, i18n] = useTranslation()

	const creditButtons = parameters?.creditCards?.clientButtonName || []
	const isAndroidDevice = (deviceType !== 'OLD_CASHREGISTER') && (deviceType) //!!deviceIdentity
	const isWindowsDevice = deviceType === 'OLD_CASHREGISTER'

	const FontSize = () => {
		const sizeOptions = []

		for(let i = 6; i <= 20; i++) {
			sizeOptions.push({ value: i, label: i })
		}

		return sizeOptions
	}
	let specialDesignType = parameters?.specialDesignType
	const windowsModeEnabled = specialDesignType !== null && specialDesignType !== ''


	const tareFields = useMemo(() => {
		const out = []
		for (let i = 0; i < 10; i++) {
			const item = (
				<React.Fragment key={i}>
					<Row middle='xs'>
						<Col xs={3}>
							<Text field={`parameters.weightTara.names[${i}].value`} />
						</Col>
						<Col xs={1}>
							<Text field={`parameters.weightTara.values[${i}].value`} />
						</Col>
						<Col xs={2}>
							<Checkbox field={`parameters.weightTara.isAutoSelect[${i}].value`} />
						</Col>
						<Col xs={3}>
							<Text field={`parameters.weightTara.names[${i + 10}].value`} />
						</Col>
						<Col xs={1}>
							<Text field={`parameters.weightTara.values[${i + 10}].value`} />
						</Col>
						<Col xs={2}>
							<Checkbox field={`parameters.weightTara.isAutoSelect[${i + 10}].value`} />
						</Col>
					</Row>
					{i !== 9 && <Divider height={36} />}
				</React.Fragment>
			)
			out.push(item)
		}

		return(
			<>
				<Row>
					<Col xs={3}>
						<p className='form-group-label'>{t('options_section.fields.tare_name', { value: '1 - 10' })}</p>
					</Col>
					<Col xs={1}>
						<p className='form-group-label'>{t('options_section.fields.tare', { value: '1 - 10' })}</p>
					</Col>
					<Col xs={2}>
						<p className='form-group-label'>{t('options_section.fields.add_tare_auto', { value: '1 - 10' })}</p>
					</Col>
					<Col xs={3}>
						<p className='form-group-label'>{t('options_section.fields.tare_name', { value: '11 - 20' })}</p>
					</Col>
					<Col xs={1}>
						<p className='form-group-label'>{t('options_section.fields.tare', { value: '11 - 20' })}</p>
					</Col>
					<Col xs={2}>
						<p className='form-group-label'>{t('options_section.fields.add_tare_auto', { value: '11 - 20' })}</p>
					</Col>
				</Row>
				{out}
			</>
		)
	}, [t])

	const handleSubmit = originalVals => {
		const vals = cloneDeep(originalVals)
		const weightTara = vals?.parameters?.weightTara || {}
		Object.keys(weightTara).forEach(field => {
			if(Array.isArray(weightTara[field])) {
				weightTara[field] = weightTara[field].reduce((outArr, item, i) => {
					const value = item?.value
					if(typeof value !== 'undefined') {
						outArr.push({ value, position: i })
					}
					return outArr
				}, [])
			}
		})


		let designType = vals?.parameters?.regularWindowsModeEnabled
		if(designType === true && !vals?.parameters?.specialDesignType) {
			set(vals, 'parameters.specialDesignType', 'WindowsProductAndCategoryStyle')
		}else if(designType === false) {
			set(vals, 'parameters.specialDesignType', null)
		}
		unset(vals, 'parameters.regularWindowsModeEnabled')

		const creditPositionPath = 'parameters.paymentTerminal'
		const { creditPositionInfo = {}, ...restParams } = vals?.parameters?.paymentTerminal || {}
		const { position } = creditPositionInfo
		// 'position' values starts from 1 (not 0) in DB
		const isPositionSet = position || position === 0
		const modifiedVals = set(vals, creditPositionPath, { ...restParams, ...isPositionSet && { creditPositionInfo: position + 1 } })
		const tseIsActive = vals?.parameters?.tseIsActive
		const tsePrintQRCode = vals?.parameters?.tsePrintQRCode
		const eftTransaction = vals?.parameters?.paymentTerminal?.notOpenCashDrawerAfterEFTTransaction
		if(typeof tseIsActive === 'number') {
			set(modifiedVals, 'parameters.tseIsActive', !!tseIsActive)
		}
		if(typeof tsePrintQRCode === 'number') {
			set(modifiedVals, 'parameters.tsePrintQRCode', !!tsePrintQRCode)
		}
		if(typeof eftTransaction === 'boolean') {
			set(modifiedVals, 'parameters.paymentTerminal.notOpenCashDrawerAfterEFTTransaction', !eftTransaction)
		}
		if(vals.parameters?.gloryTerminalPassword === formData.parameters?.gloryTerminalPassword) {
			unset(modifiedVals, 'parameters.gloryTerminalPassword')
		}
		updateItem(modifiedVals, {
			optimisticResponse: optimistic => {
				const newOptimistic = cloneDeep(optimistic)
				set(newOptimistic, `${creditPositionPath}.creditPositionInfo`, {
					...creditPositionInfo,
					__typename: 'PaymentTerminalPositionInfo'
				})
				return newOptimistic
			}
		})
	}

	let statusLabel
	if(isAndroidDevice && !deviceIdentity) {
		statusLabel = <StyledLabesStatus color='warningColor'>{t('options_section.strings.is_not_paired')}</StyledLabesStatus>
	} else {
		switch(status) {
			case 'READY':
				statusLabel = <StyledLabesStatus color='greenColor'>{t('common.strings.ready')}</StyledLabesStatus>
				break
			case 'UPDATING_DATA':
				statusLabel = (
					<>
						<StyledLabesStatus color='warningColor'>{t('common.strings.updating_data')}</StyledLabesStatus>
						<LoadingIcon style={{ verticalAlign: 'middle', margin: '3px', height: '14px' }}/>
					</>
				)
				break
			case 'ERROR':
				statusLabel = (
					<>
						<StyledLabesStatus color='errorColor'>{t('options_section.strings.copy_settings_error')}</StyledLabesStatus>
						<Button text={t('common.buttons.retry')} status='cancelation'
							style={{
								minWidth: 'unset',
								height: '20px',
								fontSize: '12px',
								padding: '0 5px',
								margin: '0 3px'
							}}
							onClick={e =>{
								e.stopPropagation()
								copyCashRegisterSettings()
							}}
						/>
					</>
				)
				break
			default:
				statusLabel = null
		}
	}
	const androidLabel = (
		<div>
			{t('options_section.strings.android')} {statusLabel}
		</div>
	)
	const SpecialDesignCard = ({ img, description }) =>(
		<div className={globalStyles.windowsModeOptionsCard}>
			<div className={globalStyles.background}>
				<img onClick={() => {
					setShowFullPicture({ image: img, isEnabled: true })
				}
				} src={img} className={globalStyles.img} alt={''}/>
			</div>
			<div className={globalStyles.background}>
				<h4 className={globalStyles.description}>{description}</h4>
			</div>
		</div>
	)

	return (
		<ExpandableBlocks defaultExpanded={Number(!isAndroidDevice)}>
			{isAndroidDevice &&
			<ContentBlock label={androidLabel}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Android cashRegister={data} deviceIdentity={deviceIdentity} deviceType={deviceType} id={id} shopId={shopId} />
				</Form>
			</ContentBlock>
			}
			{deviceType === 'SELF_CHECKOUT' &&
			<ContentBlock label='selfCheckout'>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>

					<SelfCheckout
						itemId={id}
						updateItem={updateItem}
						formData={formData}
					/>
				</Form>
			</ContentBlock>
			}

			<ContentBlock label={t('options_section.strings.printer')}>
				<Printers data={data} formData={formData} handleSubmit={handleSubmit} updateItem={updateItem}/>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.payment_terminals')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<PaymentTerminals creditButtons={creditButtons} />
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.fields.tse')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Tse formData={formData} data={data} />
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.touch_screen')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Row>
						{!isAndroidDevice &&

							<Col xs>
								<NumberInput
									field='parameters.categoryGridX'
									label={t('options_section.fields.horizontal_buttons_number')}
								/>
								<NumberInput
									field='parameters.categoryGridY'
									label={t('options_section.fields.vertical_buttons_number')}
								/>
							</Col>
						}

						<Col xs>
							<NumberInput
								field='parameters.idleMinutesBeforeLock'
								label={t('options_section.fields.logout_after_minutes')}
							/>
						</Col>
						<Col xs>
							<Select
								field='parameters.displayArticle'
								label={t('options_section.fields.display_article')}
								options={DisplayArticlesValues(t)}
							/>
						</Col>
					</Row>
					<Divider/>
					{
						isWindowsDevice && (
							<>
								<Row>
									<Col xs>
										<Select
											field='parameters.categoryFont.fontName'
											label={t('options_section.fields.category_font')}
											options={Fonts}
										/>
									</Col>
									<Col xs>
										<Select
											field='parameters.categoryFont.size'
											label={t('options_section.fields.category_font_size')}
											options={FontSize()}
										/>
									</Col>
								</Row>
								<Divider/>
								<Row>
									<Col xs>
										<Select
											field='parameters.productFont.fontName'
											label={t('options_section.fields.product_font')}
											options={Fonts}
										/>
									</Col>
									<Col xs>
										<Select
											field='parameters.productFont.size'
											label={t('options_section.fields.product_font_size')}
											options={FontSize()}
										/>
									</Col>
								</Row>
							</>
						)
					}
					<Divider />
					<Checkbox field='parameters.autoSelectFirstCategory' label={t('options_section.fields.activate_categoty_after_payment')} />
					<Divider />
					<Checkbox field='parameters.regularWindowsModeEnabled' initialValue={windowsModeEnabled} label={t('options_section.fields.windows_look_grid')} />
					<Divider height={10} />
					{ windowsModeEnabled &&
					((!showFullPicture.isEnabled) ? (<Col type='row'>
						<Divider height={10}/>
						<Row>
							<Col xs>
								<SpecialDesignCard img={windowsLookRegularImage} description={t('options_section.fields.windows_regular_option')}/>
							</Col>
							<Col xs>
								<SpecialDesignCard img={windowsLookPagingImage} description={t('options_section.fields.windows_option_with_paging')}/>
							</Col>
						</Row>
						<Row>
							<SmartRadioGroup field='parameters.specialDesignType' value={specialDesignType}>
								<Col xs>
									<div className={globalStyles.singleRadio}>
										<Radio value='WindowsProductAndCategoryStyle' label='1'/>
									</div>
								</Col>
								<Col xs>
									<div className={globalStyles.singleRadio}>
										<Radio value='WindowsProductAndCategoryStyleWithPaging' label='2'/>
									</div>
								</Col>
							</SmartRadioGroup>
						</Row>
					</Col>) : (<Modal unmount={() => setShowFullPicture({ image: null, isEnabled: false })} >
						<img src={showFullPicture.image} style={{ width: '100%', margin: 'auto auto' }} alt=''/>
					</Modal>))}
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.customer_display')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>

					<Checkbox field='parameters.isQRCodeBillEnabled' label={t('options_section.fields.activate_qr_receipt_android')} />
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.scales')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Row>
						<Col xs>
							<Select
								field='parameters.scale'
								label={t('options_section.fields.manufacturer')}
								remoteOptions={{
									query: loadAppConstants,
									variables: {
										language: i18n.language,
										type: 8
									},
									modifyOptions: opts => opts.reduce((out, item) => {
										if(item) {
											const { label, value } = item
											const numVal = Number(value)
											// In CA web only 'Not selected'(-1) and 'Dibal (Dialog 06 Protokoll)'(5) are available
											if(numVal === -1 || numVal === 5) {
												out.push({ label, value: numVal })
											}
										}
										return out
									}, [])
								}}
							/>
						</Col>
						<StyledColAlignCenter xs={6} $centerCheckboxLabel>
							<Checkbox
								field='parameters.scaleDisplayAlwaysVisible'
								label={<>
									{t('options_section.fields.scale_display_always_visible')}.
									<br />
									{t('options_section.strings.contact_support')}.
								</>}
							/>
						</StyledColAlignCenter>
					</Row>
					<Divider/>
					{tareFields}
				</Form>
			</ContentBlock>

			<ContentBlock label={t('options_section.strings.cash_drawer')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Row>
						<Col xs={6}>
							<Checkbox field='parameters.autoCloseBill' label={t('options_section.fields.auto_close_bill')}/>
						</Col>
						<Col xs={6}>
							{formData?.parameters?.printer?.name === 'SUNMI Cloud Printer' &&
								<Checkbox field='parameters.isCashierHasManyDrawers' label={t('options_section.fields.two_cash_drowers_mode')} />
							}
						</Col>
					</Row>
				</Form>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default Hardware
